/**
 * Pure ordinal-extraction for video / audio variant histories.
 *
 * `getVariantLabel` (display) and `buildClipDownloadFilename` (download
 * filename) both consume the integers returned here, so the visible chip and
 * the saved filename can never disagree on numbering.
 *
 * No React, no DOM imports — safe to use from any module.
 */

import {
  type BasicImage,
  type BasicAssetMetadata,
  type GeneratedAssetMetadata,
} from '../../../../generated/nodeDataTypes'

/**
 * Audio-remix entries carry extra runtime keys on `metadata` (`type`,
 * `source_original_index`, etc.) that the generated `GeneratedAssetMetadata`
 * shape does not model. Intersect with the known optional keys so we can read
 * them without type casts.
 */
export type RemixMetadata = GeneratedAssetMetadata & {
  type?: string
  timestamp?: number
  source_original_index?: number
  sourceOriginalIndex?: number
  source_original_path?: string
  sourceOriginalPath?: string
  /**
   * RFC-0986 legacy metadata: `"unified"` marks variants produced by the old
   * in-node audio lane renderer so old histories can still label them
   * distinctly from older Audio Remix variants.
   */
  editor?: string
}

export type VideoHistoryResult = {
  id?: string
  local_path?: string
  metadata?: BasicAssetMetadata
}

export interface VideoHistoryRecord {
  user_prompt?: string
  result?: VideoHistoryResult
  refs?: BasicImage[]
  metadata?: RemixMetadata
}

export type VideoHistoryEntry = VideoHistoryRecord | null

export function metadataType(metadata: RemixMetadata | undefined): string | undefined {
  return metadata?.type
}

/** RFC-0986: authoring surface (e.g. `"unified"`) that produced the variant. */
export function metadataEditor(metadata: RemixMetadata | undefined): string | undefined {
  return metadata?.editor
}

export function metadataNumber(
  metadata: RemixMetadata | undefined,
  ...keys: Array<'source_original_index' | 'sourceOriginalIndex'>
): number | undefined {
  if (!metadata) return undefined
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
  }
  return undefined
}

export function metadataString(
  metadata: RemixMetadata | undefined,
  ...keys: Array<'source_original_path' | 'sourceOriginalPath'>
): string | undefined {
  if (!metadata) return undefined
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'string' && value.length > 0) return value
  }
  return undefined
}

export interface VariantOrdinals {
  /** 1-based ordinal of this entry among originals (null for remix entries). */
  originalOrdinal: number | null
  /** 1-based ordinal of this remix within its source-original (null when not a remix). */
  remixOrdinal: number | null
  /** Ordinal of the original this remix descends from; null when no source can be resolved. */
  sourceOriginalOrdinal: number | null
}

const NO_ORDINALS: VariantOrdinals = {
  originalOrdinal: null,
  remixOrdinal: null,
  sourceOriginalOrdinal: null,
}

/**
 * Compute ordinals for EVERY entry in one pass.
 *
 * Histories are unbounded (the backend's old 20-entry cap was removed — a
 * front-trim silently renumbered variants), so this must not be quadratic per
 * cell. Remix-source resolution is memoized and remix counts accumulate in a
 * single forward sweep.
 */
function computeAllVariantOrdinals(history: VideoHistoryEntry[]): VariantOrdinals[] {
  const originalOrdinals = new Map<number, number>()
  let originalCount = 0

  for (let i = 0; i < history.length; i++) {
    const entry = history[i]
    if (!entry?.result) continue
    const isRemix = metadataType(entry.metadata) === 'audio_remix'
    if (!isRemix) {
      originalCount++
      originalOrdinals.set(i, originalCount)
    }
  }

  const resolveRemixSourceIndexUncached = (idx: number): number | null => {
    const entry = history[idx]
    const sourceIdx = metadataNumber(entry?.metadata, 'source_original_index', 'sourceOriginalIndex')
    if (sourceIdx != null && originalOrdinals.has(sourceIdx)) {
      return sourceIdx
    }
    const sourcePath = metadataString(entry?.metadata, 'source_original_path', 'sourceOriginalPath')
    if (sourcePath) {
      const matchingIndex = history.findIndex(
        (candidate) =>
          Boolean(candidate?.result) &&
          metadataType(candidate?.metadata) !== 'audio_remix' &&
          candidate?.result?.local_path === sourcePath,
      )
      if (matchingIndex >= 0) return matchingIndex
    }
    for (let i = idx - 1; i >= 0; i--) {
      if (originalOrdinals.has(i)) return i
    }
    return null
  }

  const sourceIndexCache = new Map<number, number | null>()
  const resolveRemixSourceIndex = (idx: number): number | null => {
    const cached = sourceIndexCache.get(idx)
    if (cached !== undefined) return cached
    const resolved = resolveRemixSourceIndexUncached(idx)
    sourceIndexCache.set(idx, resolved)
    return resolved
  }

  // Running remix count per source-original index (-1 == unresolvable source).
  const remixCounts = new Map<number, number>()
  const ordinals: VariantOrdinals[] = new Array(history.length)

  for (let i = 0; i < history.length; i++) {
    const entry = history[i]
    const isRemix = metadataType(entry?.metadata) === 'audio_remix'
    if (!isRemix) {
      ordinals[i] = {
        originalOrdinal: originalOrdinals.get(i) ?? null,
        remixOrdinal: null,
        sourceOriginalOrdinal: null,
      }
      continue
    }
    const sourceIndex = resolveRemixSourceIndex(i)
    const key = sourceIndex ?? -1
    const seenBefore = remixCounts.get(key) ?? 0
    // Entries without a result are labelled but never counted — matches the
    // original per-entry implementation this replaced.
    const count = entry?.result ? seenBefore + 1 : seenBefore
    if (entry?.result) remixCounts.set(key, count)
    ordinals[i] = {
      originalOrdinal: null,
      remixOrdinal: Math.max(1, count),
      sourceOriginalOrdinal: sourceIndex != null ? originalOrdinals.get(sourceIndex) ?? null : null,
    }
  }

  return ordinals
}

/**
 * Ordinals for every entry, memoized on the history array's identity.
 *
 * Callers render one cell per entry and each cell needs its own ordinals; the
 * cache keeps the whole grid at one O(n) sweep per history array instead of one
 * per cell.
 */
const ordinalsByHistory = new WeakMap<VideoHistoryEntry[], VariantOrdinals[]>()

export function getAllVariantOrdinals(history: VideoHistoryEntry[]): VariantOrdinals[] {
  const cached = ordinalsByHistory.get(history)
  if (cached) return cached
  const computed = computeAllVariantOrdinals(history)
  ordinalsByHistory.set(history, computed)
  return computed
}

/**
 * Extract ordinals for entry `realIdx` from a video/audio variant history.
 *
 * Pure integer logic — display ("v1 remix 1") and filename ("v01_remix_01") are
 * both built from these integers.
 */
export function getVariantOrdinals(history: VideoHistoryEntry[], realIdx: number): VariantOrdinals {
  return getAllVariantOrdinals(history)[realIdx] ?? NO_ORDINALS
}
